import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@/components/ui/Alert';
import useFundContext from '@/contexts/Fund/useFundContext';
import { queryClient } from '@/hooks/QueryClient';
import ReturnedPaymentDialog from './ReturnedPaymentDialog';
import useReturnedPayments, { returnedPaymentsUrl } from './useReturnedPayments';

interface ReturnedPaymentNewRequestNoticeProps {
  /** The supported the new request is for. */
  supportedId: number | undefined;
  /** After the account was replaced - the requests list behind the wizard changed. */
  onChanged: () => void;
}

/**
 * HAT-1395: in production, two thirds of the returned one-time requests were filed again as new
 * requests - the only way out before this flow. Kept up after it, that habit pays the support twice:
 * once on the new request and once more when the old one is fixed. So when the coordinator starts a
 * request for a supported whose support is waiting for another account, the wizard says so first and
 * offers the fix, in the same window as everywhere else.
 */
const ReturnedPaymentNewRequestNotice = ({ supportedId, onChanged }: ReturnedPaymentNewRequestNoticeProps) => {
  const { t } = useTranslation();
  const { currentFund } = useFundContext();
  const { groups } = useReturnedPayments();
  const [open, setOpen] = useState(false);

  const group = groups.find((g) => g.supportedId === supportedId);
  if (!group || !currentFund) return null;
  // Only a request whose payment bounced is "returned"; the others merely pay to the deleted account.
  const bounced = group.requests.filter((r) => r.months.some((m) => m.returned)).length;

  return (
    <>
      <Alert
        type="warning"
        dismissible={false}
        title={t('returnedPayment.newRequestNoticeTitle')}
        body={bounced > 0 ? t('returnedPayment.newRequestNoticeBody', { count: bounced }) : t('returnedPayment.newRequestNoticeBodyDeletedOnly')}
        action={{ label: t('returnedPayment.menuLabel'), onClick: () => setOpen(true) }}
      />
      {open && (
        <ReturnedPaymentDialog
          fundId={currentFund.id}
          group={group}
          onClose={() => setOpen(false)}
          onChanged={() => {
            queryClient.invalidateQueries({ queryKey: [returnedPaymentsUrl(currentFund.id)] });
            onChanged();
          }}
        />
      )}
    </>
  );
};

export default ReturnedPaymentNewRequestNotice;
